// src/modules/units/tyres/views/import/tyreimport.view.js
//
// Pure markup for the tyre import wizard: File → Map → Review → Import. Data in,
// HTML strings out — no fetch, no DOM, no state. The orchestrator (tyreimport.js)
// owns the state and decides which of these to (re)draw; each builder fills one
// named slot, so a change repaints only the part it affects (a mapping change must
// not rebuild the select the user is still focused on).
//
// Every value that came from a spreadsheet or the server passes through esc().

import { esc } from '../../shared/tyre-format.js';
import { columnLetter } from '../../shared/tyre-import-schema.js';
import { fieldGroups, IMPORT_ACTION, EXISTING_MODE } from '../../shared/tyre-import-validate.js';
import { IMPORT_RESULT } from '../../shared/tyre-import-run.js';

export const IMPORT_STEPS = Object.freeze([
  { id: 'file', label: 'Choose file' },
  { id: 'map', label: 'Map columns' },
  { id: 'review', label: 'Review' },
  { id: 'run', label: 'Import' }
]);

export const PAGE_SIZE = 50;

const MUTED = '<span class="tm-muted">—</span>';
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const fmtInt = (n) => Number(n || 0).toLocaleString('en-ZA');

// ---------------------------------------------------------------------------
// Frame
// ---------------------------------------------------------------------------

export function importShellHTML() {
  return `
    <div class="tm-import">
      <div class="tm-form-head tm-imp-head">
        <div>
          <h2 class="tm-form-title">Import tyres</h2>
          <p class="tm-form-sub">Bring a tyre list in from an Excel workbook or a CSV file.</p>
        </div>
        <ol class="tm-imp-steps" data-imp-steps aria-label="Import steps"></ol>
      </div>
      <div class="tm-form-msg" data-imp-msg hidden role="status"></div>
      <div class="tm-imp-body" data-imp-body></div>
      <div class="tm-imp-footer" data-imp-footer></div>
    </div>`;
}

export function stepsHTML(current) {
  const idx = IMPORT_STEPS.findIndex((s) => s.id === current);
  return IMPORT_STEPS.map((s, i) => {
    const state = i < idx ? 'is-done' : i === idx ? 'is-current' : '';
    const aria = i === idx ? ' aria-current="step"' : '';
    return `<li class="tm-imp-step ${state}"${aria}><span class="tm-imp-step-n">${i + 1}</span><span class="tm-imp-step-l">${esc(s.label)}</span></li>`;
  }).join('');
}

function footer(left = '', right = '') {
  return `<div class="tm-imp-footer-l">${left}</div><div class="tm-imp-footer-r">${right}</div>`;
}

// ---------------------------------------------------------------------------
// Step 1 — file + yard
// ---------------------------------------------------------------------------

function yardOptions(yards, yardId) {
  const opts = yards.map((y) => {
    const label = y.customerName ? `${y.name} · ${y.customerName}` : y.name;
    const sel = String(y.Id) === String(yardId) ? ' selected' : '';
    return `<option value="${esc(y.Id)}"${sel}>${esc(label)}</option>`;
  });
  return ['<option value="">Select a yard</option>', ...opts].join('');
}

/**
 * @param {object} s
 * @param {'loading'|'ready'|'error'} s.yardsStatus
 * @param {string} [s.yardsError]
 * @param {Array} s.yards
 * @param {*} s.yardId
 * @param {string} s.fileName
 * @param {boolean} s.reading
 * @param {Array<{name:string}>} s.sheets
 * @param {number} s.sheetIndex
 * @param {number} s.headerRow  0-based
 * @param {number} s.rowCount   rows in the chosen sheet
 * @param {boolean} [s.truncated]
 */
export function fileStepHTML(s) {
  let yardControl;
  if (s.yardsStatus === 'loading') {
    yardControl = '<select class="tm-input" disabled><option>Loading yards…</option></select>';
  } else if (s.yardsStatus === 'error') {
    yardControl = `<div class="tm-form-msg tm-form-msg-error">${esc(s.yardsError || 'Could not load yards.')} <button type="button" class="tm-imp-linkbtn" data-imp-retry-yards>Try again</button></div>`;
  } else if (!s.yards.length) {
    yardControl = '<select class="tm-input" disabled><option>No yards yet — create a yard first</option></select>';
  } else {
    yardControl = `<select id="tm-imp-yard" class="tm-input" data-imp-yard>${yardOptions(s.yards, s.yardId)}</select>`;
  }

  const hasFile = !!s.fileName && s.sheets?.length;
  const drop = `
    <label class="tm-imp-drop${hasFile ? ' has-file' : ''}" data-imp-drop>
      <input type="file" class="tm-imp-file" data-imp-file
             accept=".xlsx,.xlsm,.csv,.txt,.tsv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" />
      <span class="tm-imp-drop-ic" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M9 13l2 2 4-4"/></svg>
      </span>
      ${s.reading
      ? '<span class="tm-imp-drop-t">Reading the file…</span>'
      : hasFile
        ? `<span class="tm-imp-drop-t"><b>${esc(s.fileName)}</b></span><span class="tm-imp-drop-s">${esc(plural(Math.max(0, s.rowCount - s.headerRow - 1), 'data row'))} · Click or drop to choose a different file</span>`
        : '<span class="tm-imp-drop-t"><b>Choose a spreadsheet</b> or drop it here</span><span class="tm-imp-drop-s">Excel workbook (.xlsx) or CSV · up to 5 000 rows</span>'}
    </label>`;

  const sheetPicker = hasFile && s.sheets.length > 1 ? `
      <div class="tm-form-row">
        <label class="tm-form-label" for="tm-imp-sheet">Worksheet</label>
        <select id="tm-imp-sheet" class="tm-input" data-imp-sheet>
          ${s.sheets.map((sh, i) => `<option value="${i}"${i === s.sheetIndex ? ' selected' : ''}>${esc(sh.name)}</option>`).join('')}
        </select>
      </div>` : '';

  const headerPicker = hasFile ? `
      <div class="tm-form-row">
        <label class="tm-form-label" for="tm-imp-header">Header row <span class="tm-field-note">(found automatically)</span></label>
        <input id="tm-imp-header" type="number" class="tm-input" min="1" max="${Math.max(1, s.rowCount)}" step="1"
               value="${s.headerRow + 1}" data-imp-header />
      </div>` : '';

  const truncated = s.truncated
    ? '<div class="tm-form-msg tm-form-msg-warn">Only the first 5 000 rows were read. Split larger lists into several files.</div>'
    : '';

  return `
    <div class="tm-imp-grid">
      <section class="tm-form-grid tm-form-card tm-imp-card">

        <div class="tm-form-row tm-form-row-wide">
          <label class="tm-form-label" for="tm-imp-yard">Yard<span class="tm-req" aria-hidden="true">*</span></label>
          ${yardControl}

        </div>
      </section>
      <section class="tm-form-grid tm-form-card tm-imp-card">
        <h3 class="tm-form-card-title">Spreadsheet</h3>
        <div class="tm-form-row tm-form-row-wide">${drop}</div>
        ${templateRowHTML(s.template)}
        ${sheetPicker}
        ${headerPicker}
      </section>
    </div>
    ${truncated}
    <div data-imp-preview></div>`;
}

/**
 * The "download a template" offer under the drop zone. The template is an Excel
 * workbook laid out like the tyre form, with this customer's yards, suppliers and
 * tyre catalogue as dropdowns (tyreimport-template.js).
 * @param {{busy?:boolean, disabled?:boolean}} [t]
 */
export function templateRowHTML(t = {}) {
  const off = t.busy || t.disabled ? ' disabled' : '';
  return `
      <div class="tm-form-row tm-form-row-wide tm-imp-template">
        <p class="tm-imp-template-t">No spreadsheet yet? Start from the <b>import template</b>. It is laid out like the New tyre form, with dropdowns for your yards, suppliers and tyre catalogue.</p>
        <button type="button" class="tm-btn-secondary tm-imp-template-btn" data-imp-template${off}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v10"/><path d="M8 10l4 4 4-4"/><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/></svg>
          ${t.busy ? 'Preparing template…' : 'Download template (.xlsx)'}
        </button>
      </div>`;
}

/** Raw preview of the sheet from the header row down (first `limit` data rows). */
export function previewHTML(rows = [], headerRow = 0, limit = 8) {
  if (!rows.length) return '';
  const slice = rows.slice(headerRow, headerRow + 1 + limit);
  const width = Math.max(1, ...slice.map((r) => (r || []).length));
  const cols = Array.from({ length: width }, (_, i) => i);
  const cell = (v) => (v === null || v === undefined || v === '' ? '' : esc(v));
  const body = slice.map((r, i) => {
    const isHead = i === 0;
    const tag = isHead ? 'th' : 'td';
    return `<tr class="${isHead ? 'tm-imp-prev-head' : ''}"><td class="tm-imp-rownum">${headerRow + i + 1}</td>${cols.map((c) => `<${tag}>${cell((r || [])[c])}</${tag}>`).join('')}</tr>`;
  }).join('');
  return `
    <section class="tm-imp-preview">
      <h3 class="tm-imp-section-title">Preview <span class="tm-muted">— first rows as read from the file; the shaded row is used as the column headings</span></h3>
      <div class="tm-table-wrap tm-imp-scroll">
        <table class="tm-table tm-imp-table tm-imp-prev">
          <thead><tr><th class="tm-imp-rownum"></th>${cols.map((c) => `<th>${columnLetter(c)}</th>`).join('')}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </section>`;
}

export function fileFooterHTML({ canNext = false } = {}) {
  return footer(
    '<button type="button" class="tm-btn-secondary" data-imp-cancel>Cancel</button>',
    `<button type="button" class="tm-btn-primary" data-imp-next${canNext ? '' : ' disabled'}>Next: map columns</button>`
  );
}

// ---------------------------------------------------------------------------
// Step 2 — mapping
// ---------------------------------------------------------------------------

function fieldSelect(col, selected) {
  const groups = fieldGroups().map((g) => `
      <optgroup label="${esc(g.name)}">
        ${g.fields.map((f) => `<option value="${esc(f.key)}"${f.key === selected ? ' selected' : ''}>${esc(f.label)}${f.required ? ' *' : ''}</option>`).join('')}
      </optgroup>`).join('');
  return `<select class="tm-input tm-imp-map-select" data-imp-map="${col}" aria-label="Import column ${columnLetter(col)} as">
      <option value=""${selected ? '' : ' selected'}>— Don’t import —</option>${groups}
    </select>`;
}

/**
 * @param {object} s
 * @param {string[]} s.headers
 * @param {string[]} s.mapping
 * @param {Array<Array<*>>} s.samples   up to 3 sample values per column
 * @param {'update'|'skip'} s.mode
 * @param {boolean} s.remembered        mapping came from a previous import
 */
export function mapStepHTML(s) {
  const rows = s.headers.map((h, col) => {
    const samples = (s.samples[col] || []).filter((v) => v !== null && v !== undefined && v !== '');
    const sampleHtml = samples.length
      ? samples.map((v) => `<span class="tm-imp-sample">${esc(v)}</span>`).join('')
      : '<span class="tm-muted">empty</span>';
    return `
      <tr data-imp-map-row="${col}" class="${s.mapping[col] ? 'is-mapped' : ''}">
        <td class="tm-imp-col"><span class="tm-imp-colletter">${columnLetter(col)}</span>${h ? esc(h) : '<span class="tm-muted">(no heading)</span>'}</td>
        <td class="tm-imp-samples">${sampleHtml}</td>
        <td class="tm-imp-mapto">${fieldSelect(col, s.mapping[col])}</td>
      </tr>`;
  }).join('');

  const note = s.remembered
    ? '<p class="tm-imp-note">Mapping restored from the last time you imported a file with these columns.</p>'
    : '<p class="tm-imp-note">Columns were matched on their headings. Check each one, and set any column you don’t want to “Don’t import”.</p>';

  return `
    <div class="tm-imp-map">
      ${note}
      <div class="tm-table-wrap">
        <table class="tm-table tm-imp-table tm-imp-maptable">
          <thead><tr><th>Spreadsheet column</th><th>Sample values</th><th>Import as</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div data-imp-map-problems></div>
      <section class="tm-form-grid tm-form-card tm-imp-card tm-imp-mode">
        <h3 class="tm-form-card-title">Tyres that already exist</h3>
        <div class="tm-form-row tm-form-row-wide" role="radiogroup" aria-label="Tyres that already exist">
          <label class="tm-imp-radio">
            <input type="radio" name="tm-imp-mode" value="${EXISTING_MODE.UPDATE}" data-imp-mode${s.mode === EXISTING_MODE.UPDATE ? ' checked' : ''} />
            <span><b>Update them</b> with the spreadsheet’s values. Blank cells leave the existing value unchanged.</span>
          </label>
          <label class="tm-imp-radio">
            <input type="radio" name="tm-imp-mode" value="${EXISTING_MODE.SKIP}" data-imp-mode${s.mode === EXISTING_MODE.SKIP ? ' checked' : ''} />
            <span><b>Skip them</b> and only add tyres that are new.</span>
          </label>
        </div>
      </section>
    </div>`;
}

export function mapProblemsHTML(problems = []) {
  if (!problems.length) return '';
  return `<div class="tm-form-msg tm-form-msg-error"><ul class="tm-imp-list">${problems.map((p) => `<li>${esc(p)}</li>`).join('')}</ul></div>`;
}

export function mapFooterHTML({ canNext = false } = {}) {
  return footer(
    '<button type="button" class="tm-btn-secondary" data-imp-back>Back</button>',
    `<button type="button" class="tm-btn-primary" data-imp-next${canNext ? '' : ' disabled'}>Next: review</button>`
  );
}

// ---------------------------------------------------------------------------
// Shared: badges, filters, pager
// ---------------------------------------------------------------------------

const ACTION_META = {
  [IMPORT_ACTION.CREATE]: { label: 'New', tone: 'stock' },
  [IMPORT_ACTION.UPDATE]: { label: 'Update', tone: 'primary-blue' },
  [IMPORT_ACTION.SKIP]: { label: 'Skip', tone: 'slate' },
  [IMPORT_ACTION.ERROR]: { label: 'Error', tone: 'red' }
};

const RESULT_META = {
  [IMPORT_RESULT.CREATED]: { label: 'Created', tone: 'fit' },
  [IMPORT_RESULT.UPDATED]: { label: 'Updated', tone: 'primary-blue' },
  [IMPORT_RESULT.SKIPPED]: { label: 'Skipped', tone: 'slate' },
  [IMPORT_RESULT.FAILED]: { label: 'Failed', tone: 'red' },
  pending: { label: 'Waiting', tone: 'pending' },
  notSent: { label: 'Not sent', tone: 'pending' }
};

export function actionBadge(action) {
  const m = ACTION_META[action] || ACTION_META[IMPORT_ACTION.ERROR];
  return `<span class="tm-badge tm-tone-${m.tone}">${esc(m.label)}</span>`;
}

export function resultBadge(status) {
  const m = RESULT_META[status] || RESULT_META.pending;
  return `<span class="tm-badge tm-tone-${m.tone}">${esc(m.label)}</span>`;
}

function filterChips(chips, active) {
  return `<div class="tm-imp-chips" role="group" aria-label="Filter rows">${chips
    .filter((c) => c.always || c.count > 0)
    .map((c) => `<button type="button" class="tm-imp-chip${c.id === active ? ' is-active' : ''}${c.tone ? ` tm-imp-chip-${c.tone}` : ''}" data-imp-filter="${esc(c.id)}" aria-pressed="${c.id === active}">
        ${esc(c.label)} <span class="tm-imp-chip-n">${fmtInt(c.count)}</span></button>`).join('')}</div>`;
}

export function pagerHTML(page, pages, total) {
  if (pages <= 1) return '';
  const from = page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  return `
    <div class="tm-imp-pager">
      <span class="tm-muted">Rows ${fmtInt(from)}–${fmtInt(to)} of ${fmtInt(total)}</span>
      <button type="button" class="tm-btn-secondary tm-imp-pagebtn" data-imp-page="${page - 1}"${page <= 0 ? ' disabled' : ''}>Previous</button>
      <button type="button" class="tm-btn-secondary tm-imp-pagebtn" data-imp-page="${page + 1}"${page >= pages - 1 ? ' disabled' : ''}>Next</button>
    </div>`;
}

function specLine(p = {}) {
  const spec = [p.tyreBrand || p.tyreMake, p.tyreModel, p.tyreSize].filter(Boolean).join(' · ');
  return spec ? `<span class="tm-muted">${esc(spec)}</span>` : '';
}

function detailsHTML(row) {
  const errs = row.errors.length && row.action !== IMPORT_ACTION.SKIP
    ? `<ul class="tm-imp-list tm-imp-errs">${row.errors.map((e) => `<li>${esc(e)}</li>`).join('')}</ul>` : '';
  const warns = row.warnings.length
    ? `<ul class="tm-imp-list tm-imp-warns">${row.warnings.map((w) => `<li>${esc(w)}</li>`).join('')}</ul>` : '';
  const notes = row.notes?.length
    ? `<ul class="tm-imp-list tm-imp-notes">${row.notes.map((n) => `<li>${esc(n)}</li>`).join('')}</ul>` : '';
  if (!errs && !warns && !notes) return specLine(row.payload) || MUTED;
  return `${errs}${warns}${notes}${errs || warns ? '' : `<div>${specLine(row.payload)}</div>`}`;
}

/**
 * The Review step's catalogue panel.
 * @param {object} c
 * @param {'loading'|'ready'|'error'} c.status
 * @param {Array<{label:string,rowNumbers:number[],payload:object}>} c.plan  entries to add
 * @param {number} c.missingRows
 * @param {number} c.inactiveRows
 * @param {boolean} c.canAdd   the user may create catalogue entries (administrator)
 * @param {boolean} c.add      "add them" is ticked
 */
export function catalogueReviewHTML(c = {}) {
  if (c.status === 'loading') {
    return '<div class="tm-form-msg tm-imp-cat">Checking the tyre catalogue…</div>';
  }
  if (c.status === 'error') {
    return '<div class="tm-form-msg tm-form-msg-warn tm-imp-cat">The tyre catalogue couldn’t be loaded, so tyres weren’t checked against it. The import itself is unaffected.</div>';
  }
  const plan = c.plan || [];
  const inactive = c.inactiveRows
    ? `<p class="tm-imp-cat-inactive">${plural(c.inactiveRows, 'row')} ${c.inactiveRows === 1 ? 'matches' : 'match'} an <b>inactive</b> catalogue entry — see the Catalogue filter.</p>` : '';
  if (!plan.length) {
    return inactive ? `<section class="tm-imp-cat tm-imp-cat-ok">${inactive}</section>`
      : '<section class="tm-imp-cat tm-imp-cat-ok"><p>Every tyre in this file matches the tyre catalogue.</p></section>';
  }

  const defaults = (p) => {
    const bits = [];
    if (p.payload.manufacturerNominalTreadDepth !== undefined) bits.push(`${p.payload.manufacturerNominalTreadDepth} mm nominal tread`);
    if (p.payload.manufacturerExpectedMileage !== undefined) bits.push(`${fmtInt(p.payload.manufacturerExpectedMileage)} km expected`);
    if (p.payload.minimumTreadForRetread !== undefined) bits.push(`${p.payload.minimumTreadForRetread} mm min retread`);
    return bits.length ? ` <span class="tm-muted">— ${esc(bits.join(' · '))}</span>` : '';
  };
  const list = plan.map((p) => `
      <li><b>${esc(p.label)}</b>${defaults(p)}
        <span class="tm-imp-cat-rows">${esc(plural(p.rowNumbers.length, 'tyre'))}</span></li>`).join('');

  const control = c.canAdd
    ? `<label class="tm-imp-radio tm-imp-cat-toggle">
         <input type="checkbox" data-imp-catalogue-add${c.add ? ' checked' : ''} />
         <span><b>Add ${plural(plan.length, 'entry', 'entries')} to the tyre catalogue</b> before importing, so these tyres appear as choices when adding or editing tyres.</span>
       </label>`
    : '<p class="tm-imp-cat-hint">Only administrators can add catalogue entries. The tyres still import; an administrator can add these under Setup → Tyre catalogue.</p>';

  return `
    <section class="tm-imp-cat${c.add && c.canAdd ? ' is-adding' : ' is-warn'}">
      <h3 class="tm-imp-cat-title">${plural(c.missingRows, 'tyre')} in this file ${c.missingRows === 1 ? 'isn’t' : 'aren’t'} in the tyre catalogue</h3>
      ${control}
      <details class="tm-imp-cat-details">
        <summary>${c.add && c.canAdd ? 'Entries that will be added' : 'Specifications not in the catalogue'} (${plan.length})</summary>
        <ul class="tm-imp-list tm-imp-cat-list">${list}</ul>
      </details>
      ${inactive}
    </section>`;
}

/** Run step: what happened to the catalogue phase. */
export function catalogueRunHTML(r) {
  if (!r) return '';
  if (r.running) return '<div class="tm-form-msg">Adding tyre catalogue entries…</div>';
  if (!r.ok) return `<div class="tm-form-msg tm-form-msg-warn">${esc(r.message || 'Catalogue entries were not added.')}</div>`;
  if (!r.planned) return '';
  const ok = r.created.length ? `Added ${plural(r.created.length, 'catalogue entry', 'catalogue entries')}.` : '';
  if (!r.failed.length) return `<div class="tm-form-msg tm-form-msg-ok">${esc(ok)}</div>`;
  return `
    <div class="tm-form-msg tm-form-msg-warn">
      ${esc(ok)} ${esc(plural(r.failed.length, 'catalogue entry', 'catalogue entries'))} could not be added (the tyres were still imported):
      <ul class="tm-imp-list">${r.failed.map((f) => `<li><b>${esc(f.label)}</b> — ${esc(f.reason)}</li>`).join('')}</ul>
    </div>`;
}

// ---------------------------------------------------------------------------
// Step 3 — review
// ---------------------------------------------------------------------------

export const REVIEW_FILTERS = Object.freeze({
  all: () => true,
  create: (r) => r.action === IMPORT_ACTION.CREATE,
  update: (r) => r.action === IMPORT_ACTION.UPDATE,
  skip: (r) => r.action === IMPORT_ACTION.SKIP,
  error: (r) => r.action === IMPORT_ACTION.ERROR,
  warning: (r) => r.action !== IMPORT_ACTION.ERROR && r.warnings.length > 0,
  catalogue: (r) => r.catalogue?.status === 'missing' || r.catalogue?.status === 'inactive'
});

/**
 * @param {object} s
 * @param {object} s.summary        from summarise()
 * @param {object[]} s.pageRows     the rows on this page (already filtered)
 * @param {number} s.filteredTotal
 * @param {string} s.filter
 * @param {number} s.page
 * @param {number} s.pages
 * @param {string} s.yardName
 * @param {'update'|'skip'} s.mode
 * @param {'loading'|'ready'|'error'|'capped'} s.existingStatus
 */
export function reviewStepHTML(s) {
  const sm = s.summary;
  let existingNote = '';
  if (s.existingStatus === 'loading') {
    existingNote = '<div class="tm-form-msg">Checking which tyres are already on file…</div>';
  } else if (s.existingStatus === 'error' || s.existingStatus === 'capped') {
    existingNote = '<div class="tm-form-msg tm-form-msg-warn">Couldn’t list every existing tyre, so some rows may show as New that are already on file. Each tyre is still checked just before it is saved, so nothing is overwritten by mistake.</div>';
  }

  const headline = sm.importable
    ? `<b>${fmtInt(sm.importable)}</b> of ${plural(sm.total, 'row')} will be imported into <b>${esc(s.yardName || 'the chosen yard')}</b>.`
    : `None of the ${plural(sm.total, 'row')} can be imported yet.`;
  const errNote = sm.error
    ? ` ${plural(sm.error, 'row')} with errors will be left out — fix them in the spreadsheet and import it again; tyres already imported will be matched by ID.`
    : '';

  const chips = filterChips([
    { id: 'all', label: 'All', count: sm.total, always: true },
    { id: 'create', label: 'New', count: sm.create },
    { id: 'update', label: 'Update', count: sm.update },
    { id: 'skip', label: 'Skip', count: sm.skip },
    { id: 'error', label: 'Errors', count: sm.error, tone: 'error' },
    { id: 'warning', label: 'Warnings', count: sm.warning, tone: 'warn' },
    { id: 'catalogue', label: 'Catalogue', count: sm.catalogue || 0 }
  ], s.filter);

  const body = s.pageRows.length
    ? s.pageRows.map((r) => `
        <tr class="tm-imp-r-${esc(r.action)}">
          <td class="tm-imp-rownum">${r.rowNumber}</td>
          <td class="tm-imp-id">${r.id ? `<b>${esc(r.id)}</b>` : MUTED}</td>
          <td>${actionBadge(r.action)}</td>
          <td class="tm-imp-details">${detailsHTML(r)}</td>
        </tr>`).join('')
    : '<tr><td class="tm-state-td" colspan="4"><span class="tm-state"><span class="tm-state-msg">No rows match this filter.</span></span></td></tr>';

  return `
    <div class="tm-imp-review">
      ${existingNote}
      <div data-imp-catalogue>${s.catalogue ? catalogueReviewHTML(s.catalogue) : ''}</div>
      <p class="tm-imp-headline">${headline}${esc(errNote)}</p>
      ${chips}
      <div class="tm-table-wrap">
        <table class="tm-table tm-imp-table">
          <thead><tr><th class="tm-imp-rownum">Row</th><th>Tyre ID</th><th>Action</th><th>Details</th></tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
      ${pagerHTML(s.page, s.pages, s.filteredTotal)}
    </div>`;
}

export function reviewFooterHTML({ importable = 0, errors = 0, busy = false } = {}) {
  const dl = errors
    ? `<button type="button" class="tm-btn-secondary" data-imp-download-errors>Download ${plural(errors, 'error row')}</button>`
    : '';
  return footer(
    '<button type="button" class="tm-btn-secondary" data-imp-back>Back</button>',
    `${dl}<button type="button" class="tm-btn-primary" data-imp-start${importable && !busy ? '' : ' disabled'}>Import ${plural(importable, 'tyre')}</button>`
  );
}

// ---------------------------------------------------------------------------
// Step 4 — run
// ---------------------------------------------------------------------------

export const RUN_FILTERS = Object.freeze({
  all: () => true,
  created: (r, res) => res?.status === IMPORT_RESULT.CREATED,
  updated: (r, res) => res?.status === IMPORT_RESULT.UPDATED,
  skipped: (r, res) => res?.status === IMPORT_RESULT.SKIPPED,
  failed: (r, res) => res?.status === IMPORT_RESULT.FAILED,
  notSent: (r, res) => !res
});

/**
 * @param {object} s
 * @param {number} s.done
 * @param {number} s.total
 * @param {object} s.counts        from resultCounts()
 * @param {boolean} s.running
 * @param {boolean} s.stopping
 * @param {boolean} s.stopped
 * @param {boolean} s.authLost
 * @param {Array<{row:object,res:object|undefined}>} s.pageItems
 * @param {number} s.filteredTotal
 * @param {string} s.filter
 * @param {number} s.page
 * @param {number} s.pages
 */
export function runStepHTML(s) {
  const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
  const c = s.counts;

  let status;
  if (s.running) {
    status = s.stopping
      ? `Stopping after this batch… ${fmtInt(s.done)} of ${fmtInt(s.total)} processed.`
      : `Importing… ${fmtInt(s.done)} of ${fmtInt(s.total)} processed.`;
  } else if (s.authLost) {
    status = 'Import stopped — your session expired. Sign in again, then use “Retry” for the rows that were not saved.';
  } else if (s.stopped) {
    status = `Import stopped. ${fmtInt(s.done)} of ${fmtInt(s.total)} rows were processed; the rest were not sent.`;
  } else {
    status = c.failed
      ? `Finished with ${plural(c.failed, 'failure')}. The other rows were saved.`
      : 'Import complete.';
  }
  const tone = s.running ? '' : (s.authLost || c.failed ? ' tm-form-msg-warn' : (s.stopped ? ' tm-form-msg-warn' : ' tm-form-msg-ok'));

  const chips = filterChips([
    { id: 'all', label: 'All', count: s.total, always: true },
    { id: 'created', label: 'Created', count: c.created },
    { id: 'updated', label: 'Updated', count: c.updated },
    { id: 'skipped', label: 'Skipped', count: c.skipped },
    { id: 'failed', label: 'Failed', count: c.failed, tone: 'error' },
    { id: 'notSent', label: s.running ? 'Waiting' : 'Not sent', count: c.notSent }
  ], s.filter);

  const body = s.pageItems.length
    ? s.pageItems.map(({ row, res }) => `
        <tr class="tm-imp-res-${esc(res?.status || 'pending')}">
          <td class="tm-imp-rownum">${row.rowNumber}</td>
          <td class="tm-imp-id"><b>${esc(row.id)}</b></td>
          <td>${resultBadge(res ? res.status : (s.running ? 'pending' : 'notSent'))}</td>
          <td class="tm-imp-details">${res?.message ? esc(res.message) : specLine(row.payload) || MUTED}</td>
        </tr>`).join('')
    : '<tr><td class="tm-state-td" colspan="4"><span class="tm-state"><span class="tm-state-msg">No rows match this filter.</span></span></td></tr>';

  return `
    <div class="tm-imp-run">
      ${catalogueRunHTML(s.catalogueRun)}
      <div class="tm-form-msg${tone}" role="status" aria-live="polite">${esc(status)}</div>
      <div class="tm-imp-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}" aria-label="Import progress">
        <div class="tm-imp-progress-bar${s.running ? ' is-running' : ''}" style="width:${pct}%"></div>
      </div>
      ${chips}
      <div class="tm-table-wrap">
        <table class="tm-table tm-imp-table">
          <thead><tr><th class="tm-imp-rownum">Row</th><th>Tyre ID</th><th>Result</th><th>Details</th></tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
      ${pagerHTML(s.page, s.pages, s.filteredTotal)}
    </div>`;
}

export function runFooterHTML({ running = false, stopping = false, failed = 0, notSent = 0, yardName = '' } = {}) {
  if (running) {
    return footer('', `<button type="button" class="tm-btn-secondary" data-imp-stop${stopping ? ' disabled' : ''}>${stopping ? 'Stopping…' : 'Stop after this batch'}</button>`);
  }
  const retryN = failed + notSent;
  const retry = retryN
    ? `<button type="button" class="tm-btn-secondary" data-imp-retry>Retry ${plural(retryN, 'row')}</button>` : '';
  return footer(
    `<button type="button" class="tm-btn-secondary" data-imp-download-results>Download results</button>${retry}`,
    `<button type="button" class="tm-btn-secondary" data-imp-new>Import another file</button>
     <button type="button" class="tm-btn-primary" data-imp-open-register>View tyres${yardName ? ` in ${esc(yardName)}` : ''}</button>`
  );
}
